import { noop } from "lodash";
import * as React from "react";
import { useEffect } from "react";
import { pipe } from "../../utils/funcs";
import { Confirmation, ConfirmationProps } from "./Confirmation";

/**
 * If @param confirmBeforeAction returns false, the save action is executed immediately without rendering the modal.
 * If @param confirmBeforeAction is not specified or returns true, the modal is shown.
 * The modal automatically closes when either the save or cancel button is clicked.
 * @example
 * const action = useRenderModal((close, props) => (
 *     <ActionConfirmation
 *         {...props}
 *         confirmBeforeAction={() => ...}
 *         close={close}
 *     />
 * );
 */
export const ActionConfirmation: React.FC<
    ConfirmationProps & {
        confirmBeforeAction?: () => boolean;
        close: F<void>;
        onConfirmationClosedWithoutAction?: F<void>;
    }
> = ({ save, close, cancel = noop, confirmBeforeAction = () => true, onConfirmationClosedWithoutAction, ...props }) => {
    const doSave = pipe(save, close);
    const doCancel = pipe(cancel, close);

    useEffect(() => {
        if (!confirmBeforeAction()) {
            doSave();
        }
    }, []);

    return (
        confirmBeforeAction() && (
            <Confirmation save={doSave} cancel={doCancel} close={onConfirmationClosedWithoutAction} {...props} />
        )
    );
};
